/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

// The libraries below are fetched from a CDN at runtime and install themselves as browser
// globals, so there is nothing to import.

/** Shepherd.js — only ever probed for presence. */
declare const Shepherd: unknown;
/** `@floating-ui/core` — only ever probed for presence. */
declare const FloatingUICore: unknown;
/** `@floating-ui/dom` — only ever probed for presence. */
declare const FloatingUIDOM: unknown;
/** `body-scroll-lock` — only ever probed for presence. */
declare const bodyScrollLock: unknown;
/** Swiper — only ever probed for presence. */
declare const Swiper: unknown;
/** flatpickr — probed for presence, plus its locale registry to detect loaded l10n bundles. */
declare const flatpickr: { l10ns: Record<string, unknown> };

/**
 * A dynamically loaded library, either an entry of the built-in {@link libs} table or an inline
 * definition passed by a caller.
 */
export interface DynLibConfig {
	/**
	 * Key under which an in-flight load is de-duplicated. Optional because entries of {@link libs}
	 * are keyed by the table itself, and because the follow-up entries produced by `more()` carry
	 * no name of their own.
	 */
	name?: string;

	/** URL of the script to append to `<head>`. */
	js?: string;

	/** URL(s) of the stylesheet(s) to append to `<head>`. */
	css?: string | string[];

	/** Probe telling whether the library has finished loading and installed its global. */
	loaded: () => boolean;

	/**
	 * Produces further libraries to load alongside this one — flatpickr uses it to pull in the
	 * locale bundles named by the extra arguments passed to {@link loadDynLib}.
	 */
	more?: (...args: unknown[]) => DynLibConfig[];
}

/**
 * Everything {@link loadDynLib} accepts: a key of the built-in {@link libs} table, an inline
 * config, or an array mixing both.
 */
export type DynLibSpec = string | DynLibConfig | Array<string | DynLibConfig>;

const currentlyLoading: Record<string, Promise<unknown> | undefined> = {};

// Hard coded list of libs to load
const libs: Record<string, DynLibConfig> = {
	'shepherd.js': {
		css: 'https://cdn.jsdelivr.net/npm/shepherd.js@11.1.1/dist/css/shepherd.css',
		js: 'https://cdn.jsdelivr.net/npm/shepherd.js@11.1.1/dist/js/shepherd.min.js',
		loaded: () => typeof Shepherd !== 'undefined',
	},
	'floating-ui-core': {
		js: 'https://cdn.jsdelivr.net/npm/@floating-ui/core@1',
		loaded: () => typeof FloatingUICore !== 'undefined',
	},
	'floating-ui-dom': {
		js: 'https://cdn.jsdelivr.net/npm/@floating-ui/dom@1',
		loaded: () => typeof FloatingUIDOM !== 'undefined',
	},
	'body-scroll-lock': {
		js: 'https://cdn.jsdelivr.net/npm/body-scroll-lock@4.0.0-beta.0/lib/bodyScrollLock.min.js',
		loaded: () => typeof bodyScrollLock !== 'undefined',
	},
	swiper: {
		css: 'https://cdn.jsdelivr.net/npm/swiper@10/swiper-bundle.min.css',
		js: 'https://cdn.jsdelivr.net/npm/swiper@10/swiper-bundle.min.js',
		loaded: () => typeof Swiper !== 'undefined',
	},
	'typeform-embed': {
		js: 'https://embed.typeform.com/next/embed.js',
		css: [
			'https://embed.typeform.com/next/css/popup.css',
			'https://embed.typeform.com/next/css/popover.css',
			'https://embed.typeform.com/next/css/slider.css',
		],
		// The Typeform embed script attaches itself to `window.tf`.
		loaded: () => typeof (window as Window & { tf?: unknown }).tf !== 'undefined',
	},
	flatpickr: {
		css: 'https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css',
		js: 'https://cdn.jsdelivr.net/npm/flatpickr',
		loaded: () => typeof flatpickr !== 'undefined',
		more: (...args) => {
			const more: DynLibConfig[] = [];

			if (Array.isArray(args[0])) {
				// First argument should be a list of locales
				const locales = args[0] as string[];

				// Load locales
				locales.forEach((locale) => {
					const js = `https://cdn.jsdelivr.net/npm/flatpickr/dist/l10n/${locale}.js`;
					more.push({
						js,
						// The `typeof` guard is load-bearing: reading `l10ns` off a global that is not
						// there yet throws instead of answering "not loaded".
						loaded: () => typeof flatpickr !== 'undefined' && typeof flatpickr.l10ns[locale] !== 'undefined',
					});
				});
			}

			return more;
		},
	},
};

/**
 * Appends a stylesheet, unless one with the same href is already in the document.
 */
function appendStylesheet(href: string): void {
	if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
		return;
	}

	const link = document.createElement('link');
	link.rel = 'stylesheet';
	link.href = href;
	document.head.appendChild(link);
}

/**
 * Appends a script, unless one with the same src is already in the document. A library whose
 * `loaded()` probe has not flipped yet may still have its script in flight, and a second copy would
 * run it twice.
 */
function appendScript(src: string): void {
	if (document.querySelector(`script[src="${src}"]`)) {
		return;
	}

	const script = document.createElement('script');
	script.src = src;

	// If it's a esm module add the type attribute
	if (src.includes('.esm') || src.includes('.mjs')) {
		script.type = 'module';
	}

	document.head.appendChild(script);
}

export default function loadDynLib(lib: DynLibSpec, ...moreArgs: unknown[]): Promise<unknown> {
	if (Array.isArray(lib)) {
		return Promise.all(lib.map((l) => loadDynLib(l, ...moreArgs)));
	}

	// Only named libraries can be de-duplicated. The follow-up configs `more()` produces carry no
	// name of their own, and keying them all under the shared literal "undefined" made the second
	// locale of a request resolve against the first locale's promise instead of loading at all.
	const libName = typeof lib === 'string' ? lib : (lib.name ?? null);

	// Check if the library is already being loaded
	if (libName !== null) {
		const alreadyLoading = currentlyLoading[libName];
		if (alreadyLoading) {
			return alreadyLoading;
		}
	}

	// Filled in by exactly one of the two branches below; the emptiness check that follows them
	// is the runtime guard for callers coming from untyped JS.
	let libConf = {} as DynLibConfig;

	// If the lib is an object
	if (typeof lib === 'object') {
		libConf = { ...lib };

		if (!libConf.loaded) {
			console.warn('loadDynLib: No loaded function defined for lib', lib);
			return Promise.resolve();
		}
	}

	if (typeof lib === 'string') {
		// If lib is not in the libs object log a warning and return a resolved promise
		if (!libs[lib]) {
			console.warn('loadDynLib: lib not found', lib);
			return Promise.resolve();
		}

		libConf = { ...libs[lib] };
	}

	if (Object.keys(libConf).length === 0) {
		console.warn('loadDynLib: lib is not a string nor an valid object', lib);
		return Promise.resolve();
	}

	// Step 1: the library itself.
	let basePromise: Promise<unknown>;

	if (libConf.loaded()) {
		basePromise = Promise.resolve();
	} else {
		if (libConf.css) {
			const css = Array.isArray(libConf.css) ? libConf.css : [libConf.css];
			css.forEach((href) => appendStylesheet(href));
		}

		if (libConf.js) {
			appendScript(libConf.js);
		}

		// Poll until the library exposes itself globally.
		basePromise = new Promise<void>((resolve) => {
			const interval = setInterval(() => {
				if (libConf.loaded()) {
					clearInterval(interval);
					resolve();
				}
			}, 5);
		});
	}

	// Step 2: whatever `more()` asks for, but only once the library above has installed its global.
	// The locale bundles flatpickr pulls in are UMD add-ons that register themselves against that
	// global, so loading them alongside the base script leaves them silently unregistered.
	const loadPromise = basePromise.then(() => {
		if (!libConf.more) {
			return undefined;
		}

		return Promise.all(libConf.more(...moreArgs).map((m) => loadDynLib(m, ...moreArgs)));
	});

	if (libName !== null) {
		currentlyLoading[libName] = loadPromise;

		// Remove from dictionary once settled, so a failed load can be retried. Both outcomes are
		// handled so this bookkeeping chain never surfaces as an unhandled rejection — the failure
		// belongs to the caller's copy of the promise.
		const forget = () => {
			delete currentlyLoading[libName];
		};

		loadPromise.then(forget, forget);
	}

	return loadPromise;
}
